<app-expandable-section-box
  [isExpandable]="isExpandable"
  [isExpandedByDefault]="isExpandedByDefault"
>
  <!-- Projected unconditionally: gating this div with *ngIf would leave the header slot's
       hasChildNodes() check seeing only the anchor, hiding the box's default icon and title
       for the forms without smart linking.
       The section name never shrinks — it identifies the panel while collapsed — so below
       ~300px the button's label gives way instead, keeping its icon and its tooltip. -->
  <div class="flex items-center w-full gap-x-1.5" header>
    <i class="text-inherit shrink-0 icon icon-cubes"></i>
    <h3 class="text-sm text-inherit shrink-0 whitespace-nowrap">Related data</h3>
    <span
      *ngIf="isSmartLinkingEnabled && smartLinkPendingCount > 0"
      class="px-1.5 py-0.5 font-semibold rounded shrink-0 text-xxs bg-project-2900 text-project-300"
      [attr.aria-label]="
        smartLinkPendingCount + ' smart link suggestions to review'
      "
      data-testid="smartLinkBadge"
      >{{ smartLinkPendingCount }}</span
    >
    <!-- Busy and unavailable are styled apart though one [disabled] covers both: dimmed is how
         this bar says "you cannot use this", and the header's tan at 50% is 1.8:1, which left a
         live run reading as an empty outline. Busy takes the blue chip instead. -->
    <!-- The hint hangs on this wrapper, not the button. A disabled control receives no mouse
         events, so a [title] on the button never reaches the reader who most needs it: the
         unsaved article, where the button cannot act. disabled:pointer-events-none passes the
         hover through to here and, in the same move, stops the hover: rules below matching --
         CSS :hover matches a disabled button, which is what made this look clickable. -->
    <span
      *ngIf="isSmartLinkingEnabled"
      class="ml-auto"
      data-testid="smartLinkButtonHint"
      [title]="smartLinkButtonTitle"
    >
      <button
        mat-stroked-button
        type="button"
        class="h-7 px-2 text-xs font-medium min-w-0 disabled:pointer-events-none"
        [ngClass]="
          isSmartLinkBusy
            ? 'smart-link-busy text-project-4400 border-project-4500 bg-project-4600'
            : 'text-inherit border-project-2400/40 hover:text-project-300 hover:border-project-300 hover:bg-project-4600 disabled:opacity-50'
        "
        data-testid="smartLinkButton"
        [disabled]="isSmartLinkDisabled"
        [attr.aria-label]="smartLinkButtonTitle"
        (click)="onTriggerSmartLink($event)"
        (keydown.enter)="$event.stopPropagation()"
        (keydown.space)="$event.stopPropagation()"
      >
        <span
          *ngIf="isSmartLinkBusy; else smartLinkLabel"
          class="flex items-center min-w-0 gap-x-1"
        >
          <mat-spinner class="shrink-0" [diameter]="12"></mat-spinner>
          <span class="truncate">{{ smartLinkButtonBusyLabel }}</span>
        </span>
        <ng-template #smartLinkLabel>
          <span class="flex items-center min-w-0 gap-x-1">
            <app-ai-icon class="text-inherit shrink-0"></app-ai-icon>
            <span class="truncate">Smart Link</span>
          </span>
        </ng-template>
      </button>
    </span>
  </div>
  <div class="flex flex-col flex-1 min-w-0 py-3" expandedContent>
    <div
      *ngIf="isSmartLinkingEnabled"
      class="flex items-center w-full p-0.5 mb-3 gap-x-0.5 rounded-md bg-project-2200"
      role="tablist"
      aria-label="Related data views"
    >
      <button
        #selectedTab
        type="button"
        id="relatedDataSelectedTab"
        role="tab"
        class="flex items-baseline justify-center flex-1 px-3 py-1 text-xs rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-project-300"
        [ngClass]="
          activeView === 'selected'
            ? 'bg-white text-project-300 font-medium shadow-sm'
            : 'text-project-2400 hover:text-project-300'
        "
        [attr.aria-selected]="activeView === 'selected'"
        aria-controls="relatedDataSelectedView"
        [attr.tabindex]="activeView === 'selected' ? 0 : -1"
        data-testid="selectedViewTab"
        (click)="selectView('selected')"
        (keydown.arrowright)="selectView('suggested'); suggestedTab.focus()"
      >
        <!-- The count matches the label's size: they are separate flex items, so a smaller digit
             aligned to neither the baseline nor the cap line of the word. -->
        Selected<span class="ml-1 font-normal" data-testid="selectedTabCount">{{
          selectedCount
        }}</span>
      </button>
      <button
        #suggestedTab
        type="button"
        id="relatedDataSuggestedTab"
        role="tab"
        class="flex items-baseline justify-center flex-1 px-3 py-1 text-xs rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-project-300"
        [ngClass]="
          activeView === 'suggested'
            ? 'bg-white text-project-300 font-medium shadow-sm'
            : 'text-project-2400 hover:text-project-300'
        "
        [attr.aria-selected]="activeView === 'suggested'"
        [attr.aria-controls]="
          activeView === 'suggested' ? 'relatedDataSuggestedView' : null
        "
        [attr.tabindex]="activeView === 'suggested' ? 0 : -1"
        data-testid="suggestedViewTab"
        (click)="selectView('suggested')"
        (keydown.arrowleft)="selectView('selected'); selectedTab.focus()"
      >
        Suggested<span
          *ngIf="isSuggestedTabCountShown"
          class="ml-1 font-normal"
          data-testid="suggestedTabCount"
          >{{ smartLinkPendingCount }}</span
        >
      </button>
    </div>

    <div
      class="flex flex-col flex-1 w-full"
      [ngClass]="{ hidden: isSmartLinkingEnabled && activeView !== 'selected' }"
      [attr.id]="isSmartLinkingEnabled ? 'relatedDataSelectedView' : null"
      [attr.role]="isSmartLinkingEnabled ? 'tabpanel' : null"
      [attr.aria-labelledby]="
        isSmartLinkingEnabled ? 'relatedDataSelectedTab' : null
      "
    >
      <!-- Manager -->
      <app-related-manager
        expandedContent
        [selectedManagers]="managerListControl"
        [searchFormControl]="managerSearchFormControl"
        [searchResults]="managerSearchResults"
        [isSearching]="isSearchingManager"
        [isLoadingManagerDetails]="isLoadingManagerDetails"
        [isSurtitleButtonShown]="isShowingSurtitles"
        [smartLinkedManagerIds]="smartLinkedManagerIds"
        placeholder="Search for a manager"
        (handleRemove)="removeManager($event)"
        (handleAdd)="addManager($event)"
        (handleInfoClick)="managerInfoClick($event)"
        (handleAddToBody)="addManagerToBody($event)"
        (handlePromote)="promoteSurtitle($event, SurtitleTagType.Manager)"
        class="flex flex-col flex-1 w-full"
      ></app-related-manager>

      <!-- Funds -->
      <app-search-list
        expandedContent
        [selectedList]="fundDisplayList"
        [searchFormControl]="fundSearchFormControl"
        [searchResults]="fundSearchResults"
        [isInfoButtonShown]="true"
        [isAddButtonShown]="true"
        [isSearching]="isSearchingFund"
        [isSurtitleButtonShown]="isShowingSurtitles"
        placeholder="Search for a fund or ETF"
        (handleRemove)="removeFund($event)"
        (handleAdd)="addFund($event)"
        (handleInfoClick)="fundInfoClick($event)"
        (handleAddToBody)="addFundToBody($event)"
        (handlePromote)="promoteSurtitle($event, SurtitleTagType.Fund)"
        class="flex flex-col flex-1 w-full"
      ></app-search-list>

      <!-- Sectors -->
      <app-search-list
        expandedContent
        [selectedList]="sectorDisplayList"
        [searchFormControl]="sectorSearchFormControl"
        [searchResults]="sectorSearchResults"
        [isInfoButtonShown]="false"
        [isAddButtonShown]="false"
        [isSearching]="isSearchingSector"
        [isSurtitleButtonShown]="isShowingSurtitles"
        placeholder="Search for a sector"
        (handleRemove)="removeSector($event)"
        (handleAdd)="addSector($event)"
        (handlePromote)="promoteSurtitle($event, SurtitleTagType.Sector)"
        class="flex flex-col flex-1 w-full"
      ></app-search-list>
      <div
        class="flex items-center pb-4 text-xs gap-x-2 text-project-2100"
        *ngIf="isGettingDerivedAssetClassesAndSectors"
      >
        <mat-spinner [diameter]="12"></mat-spinner>
        <span>Recalculating derived sectors</span>
      </div>
      <!-- Asset class -->
      <app-search-list
        expandedContent
        [selectedList]="assetClassDisplayList"
        [searchFormControl]="assetClassSearchFormControl"
        [searchResults]="assetClassSearchResults"
        [isInfoButtonShown]="false"
        [isAddButtonShown]="false"
        [isSearching]="isSearchingAssetClass"
        [isSurtitleButtonShown]="isShowingSurtitles"
        placeholder="Search for an asset class"
        (handleRemove)="removeAssetClass($event)"
        (handleAdd)="addAssetClass($event)"
        (handlePromote)="promoteSurtitle($event, SurtitleTagType.AssetClass)"
        class="flex flex-col flex-1 w-full"
      ></app-search-list>
      <div
        class="flex items-center pb-4 text-xs gap-x-2 text-project-2100"
        *ngIf="isGettingDerivedAssetClassesAndSectors"
      >
        <mat-spinner [diameter]="12"></mat-spinner>
        <span>Recalculating derived asset class</span>
      </div>

      <!-- Investment trust -->
      <app-search-list
        expandedContent
        [selectedList]="investmentTrustDisplayList"
        [searchFormControl]="investmentTrustSearchFormControl"
        [searchResults]="investmentTrustSearchResults"
        [isInfoButtonShown]="false"
        [isAddButtonShown]="true"
        [isSearching]="isSearchingInvestmentTrust"
        [isSurtitleButtonShown]="isShowingSurtitles"
        placeholder="Search for an investment trust"
        (handleRemove)="removeInvestmentTrust($event)"
        (handleAdd)="addInvestmentTrust($event)"
        (handleAddToBody)="addInvestmentTrustToBody($event)"
        (handlePromote)="
          promoteSurtitle($event, SurtitleTagType.InvestmentTrust)
        "
        class="flex flex-col flex-1 w-full"
      ></app-search-list>

      <!-- Private Markets funds -->
      <ng-container *ngIf="privateMarketsFundListControl">
        <app-search-list
          expandedContent
          [selectedList]="privateMarketsFundDisplayList"
          [searchFormControl]="privateMarketsFundSearchFormControl"
          [searchResults]="privateMarketsFundSearchResults"
          [isInfoButtonShown]="false"
          [isAddButtonShown]="true"
          [isSearching]="isSearchingPrivateMarketsFund"
          [isSurtitleButtonShown]="false"
          placeholder="Search for a Private Markets fund"
          [addToBodyFormats]="privateMarketsFundBodyFormatOptions"
          (handleRemove)="removePrivateMarketsFund($event)"
          (handleAdd)="addPrivateMarketsFund($event)"
          (handleAddToBody)="addPrivateMarketsFundToBody($event)"
          (handleAddToBodyFormatted)="
            addPrivateMarketsFundToBody($event.item, $event.format)
          "
          class="flex flex-col flex-1 w-full"
        ></app-search-list>

        <!-- NOTE: inserts a placeholder the website fills in; it is not tied to the tagged
             funds above, so it is deliberately never disabled. -->
        <button
          *ngIf="isEditMode"
          type="button"
          class="flex items-center justify-center w-full h-6 px-2 mt-2 border rounded gap-x-1 text-xxs font-semibold whitespace-nowrap shrink-0 border-project-600 text-project-2400 hover:cursor-pointer hover:text-project-300 hover:border-project-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-project-300"
          data-testid="addPrivateMarketsFundsBlock"
          title="Add a block to the body where the website will render a table of all the Private Markets funds linked in this story"
          (click)="addPrivateMarketsFundsBlockToBody()"
        >
          <i class="icon icon-plus" aria-hidden="true"></i> Funds in this story
        </button>
      </ng-container>
    </div>

    <div
      *ngIf="isSmartLinkingEnabled && activeView === 'suggested'"
      id="relatedDataSuggestedView"
      role="tabpanel"
      aria-labelledby="relatedDataSuggestedTab"
      class="flex flex-col flex-1 w-full space-y-3"
      data-testid="suggestedView"
    >
      <p
        class="flex items-center gap-x-2 px-3 py-2 text-xs rounded bg-project-2200 text-project-2300"
        *ngIf="!articleId"
        data-testid="smartLinkSaveHint"
      >
        Save this content to use smart links
      </p>

      <!-- The tab is reachable before any run, and every other block in this view is gated on a
           run having happened, so a never-scanned article rendered an empty panel. -->
      <div
        class="flex flex-col items-center px-3 py-6 text-center gap-y-2"
        *ngIf="isSmartLinkIdle"
        data-testid="smartLinkIdleState"
      >
        <app-ai-icon class="text-lg text-project-300"></app-ai-icon>
        <p class="text-xs font-semibold text-project-1700">
          No suggestions yet
        </p>
        <p class="max-w-xs leading-4 text-xxs text-project-2300">
          {{ smartLinkIdleDetail }}
        </p>
        <button
          *ngIf="isEditMode"
          mat-stroked-button
          type="button"
          class="h-7 px-2 mt-1 text-xs font-medium min-w-0 text-project-300 border-project-2400/40 hover:border-project-300 hover:bg-project-4600"
          data-testid="smartLinkIdleCta"
          [disabled]="isSmartLinkGatewaySaving"
          [title]="smartLinkButtonTitle"
          (click)="onTriggerSmartLink($event)"
        >
          <span class="flex items-center min-w-0 gap-x-1">
            <app-ai-icon class="text-inherit shrink-0"></app-ai-icon>
            <span class="truncate">Smart Link</span>
          </span>
        </button>
      </div>

      <!-- No role here: the run's one announcing surface is the persistent live region at the end
           of this file. A region inserted with its text already in it is the case screen readers
           reliably fail to announce, and this row is inserted mid-run. -->
      <div
        class="relative flex items-start px-3 py-2 overflow-hidden text-xs rounded gap-x-2 bg-project-2200"
        *ngIf="isSmartLinkBusy"
        data-testid="smartLinkBusy"
        [attr.data-phase]="smartLinkStatus"
      >
        <app-ai-icon class="mt-0.5 text-sm text-project-300"></app-ai-icon>
        <span class="flex flex-col gap-y-0.5">
          <span
            class="block font-semibold leading-4 text-project-1700"
            data-testid="smartLinkBusyPhase"
            >{{ smartLinkBusyText }}</span
          >
          <span
            class="block leading-4 text-xxs text-project-2300"
            data-testid="smartLinkBusyDetail"
            >{{ smartLinkBusyDetail }}</span
          >
        </span>
        <ng-container *ngTemplateOutlet="smartLinkSweep"></ng-container>
      </div>

      <p
        class="flex items-center gap-x-2 px-3 py-2 text-xs font-medium rounded bg-project-2200 text-project-2300"
        *ngIf="smartLinkStatus === 'error'"
        role="alert"
        data-testid="smartLinksError"
      >
        {{ smartLinkErrorMessage }}
      </p>

      <p
        class="flex items-center gap-x-2 px-3 py-2 text-xs rounded bg-project-2200 text-project-2300"
        *ngIf="isSmartLinkEmptyResult"
        data-testid="smartLinksEmptyState"
      >
        No matches in this content
      </p>

      <!-- Never *ngIf'd: a live region inserted at the same moment as its text does not
           reliably announce. The visible confirmation is the linked count below. -->
      <span class="sr-only" role="status" data-testid="decisionAnnouncement">{{
        decisionAnnouncement
      }}</span>

      <!-- Outside the queue: inside it the scope control could only be found by scrolling to the
           bottom of the scroller, and it also carries the all-reviewed state, which the queue
           cannot — by then it is empty. -->
      <div
        class="overflow-hidden border rounded border-project-600 bg-project-2500"
        *ngIf="isSmartLinkSummaryShown"
        data-testid="smartLinkSummary"
      >
        <!-- Decoration, with no progressbar role: the caption at the foot of the box states the
             same fraction in words and carries the role="status" that announces it. -->
        <div
          class="h-1.5 bg-project-200"
          aria-hidden="true"
          data-testid="smartLinkProgressTrack"
        >
          <!-- Dark Blue 01, not the lighter brand cyan: against this track the cyan measures
               1.66:1, so at 6px the fill could not be told from its remainder. This clears WCAG
               1.4.11 at 3.33:1. -->
          <span
            class="block h-full bg-brand-blue-dark-1 transition-[width] duration-500 ease-out motion-reduce:transition-none"
            [style.width.%]="smartLinkReviewedPercent"
            data-testid="smartLinkProgressFill"
          ></span>
        </div>

        <!-- Equal thirds, with the labels at 10px to buy them: three natural-width scopes
             wrapped below ~260px, and the scope that wrapped was the one that vanished. -->
        <div
          class="grid grid-cols-3 px-3 pt-2.5"
          role="group"
          aria-label="Which suggestions to show"
          data-testid="smartLinkScopeSwitch"
        >
          <!-- Every scope opens, empty or not, so all three are drawn the same: one hover, one
               pointer, no aria-disabled. A zero count mutes rather than fades — opacity-40 over
               this tint left "0 linked" at 1.5:1, which reads as a rendering fault.
               The rule between scopes is a pseudo-element rather than a border-l, because the
               ngClass below sets border colour with the all-sides shorthand: on an unselected
               scope that is border-transparent, which would paint the left border away too. -->
          <button
            type="button"
            class="relative flex items-center justify-center pb-1 border-b-2 gap-x-1 text-xxs whitespace-nowrap hover:cursor-pointer before:absolute before:left-0 before:inset-y-0.5 before:w-px before:bg-project-600 first:before:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-project-300"
            *ngFor="let scope of smartLinkScopeOptions"
            [attr.data-testid]="'smartLinkScope-' + scope.key"
            [attr.aria-pressed]="smartLinkScope === scope.key"
            [ngClass]="
              smartLinkScope === scope.key
                ? 'border-brand-blue-dark-1 text-project-2000 font-medium'
                : 'border-transparent text-project-2400 hover:text-project-300'
            "
            (click)="selectSmartLinkScope(scope.key)"
          >
            <span
              class="text-xs font-semibold tabular-nums"
              [ngClass]="
                scope.count === 0 ? 'text-project-2400' : 'text-project-2000'
              "
              >{{ scope.count }}</span
            >
            {{ scope.label }}
          </button>
        </div>

        <div class="flex items-center px-3 py-1.5 mt-1 gap-x-2">
          <!-- role="status" on the caption only, not the bar: the accept beside it is not
               something to announce, and a live region should hold nothing interactive. -->
          <p
            class="text-xxs tabular-nums text-project-2300"
            role="status"
            data-testid="smartLinkReviewProgress"
          >
            <!-- Not the bar's own blue: at 3.81:1 that clears what a 6px fill needs but not the
                 4.5:1 of 10px bold text, so this takes the next step down the same ramp. -->
            <span
              class="flex items-center font-semibold gap-x-1.5 text-brand-blue-deep"
              *ngIf="isSmartLinkAllReviewed"
              data-testid="allReviewedLine"
            >
              <i class="icon icon-check" aria-hidden="true"></i>
              {{ allReviewedLabel }}
            </span>
            <ng-container *ngIf="isSmartLinkReviewProgressShown">{{
              smartLinkReviewProgressLabel
            }}</ng-container>
          </p>

          <!-- Outside the scrollport, so the widest scope stays reachable without scrolling to
               the last group. -->
          <ng-container *ngIf="isQueueAcceptShown">
            <button
              type="button"
              class="flex items-center h-6 px-2 ml-auto border rounded gap-x-1 text-xxs font-semibold shrink-0 text-project-4400 border-project-4500 bg-project-4600 hover:cursor-pointer hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-project-300"
              data-testid="queueAcceptAllBtn"
              aria-label="Accept all suggestions across every database"
              [matMenuTriggerFor]="queueAcceptScopeMenu"
            >
              <i class="icon icon-check" aria-hidden="true"></i> Accept all
              <i class="icon icon-caret-down" aria-hidden="true"></i>
            </button>
            <!-- matMenuContent, not an ngTemplateOutlet of a shared template: items declared
                 outside the mat-menu never resolve MAT_MENU_PANEL, so Material's content query
                 misses them. It also defers the scope counts to menu-open, so they cannot be
                 stale. -->
            <mat-menu
              #queueAcceptScopeMenu="matMenu"
              class="bg-white border rounded shadow-lg border-project-600"
              aria-label="How many suggestions to accept?"
            >
              <ng-template matMenuContent>
                <button
                  mat-menu-item
                  type="button"
                  class="h-8 px-3 leading-8 min-h-0 text-xxs text-project-2400 hover:cursor-pointer hover:bg-project-2500"
                  *ngFor="let scope of queueAcceptScopes"
                  [attr.data-testid]="'queueAcceptScope-' + scope.key"
                  [title]="acceptScopePreview(scope)"
                  (click)="onAcceptScope(scope)"
                >
                  {{ scope.label }}
                </button>
                <p
                  class="px-3 py-1 text-xxs text-project-1100"
                  *ngIf="queueAmbiguousCount > 0"
                  data-testid="acceptScopeAmbiguousNote"
                >
                  Excludes {{ queueAmbiguousCount }} ambiguous
                </p>
              </ng-template>
            </mat-menu>
          </ng-container>
        </div>
      </div>

      <!-- Bounded so a long run cannot push the rest of the sidebar off-screen, and because the
           group headers' sticky needs a scrollport: the panel's own overflow:hidden is one, but it
           never scrolls, so without this the sticky never fires.
           The gutter is reserved whether or not it scrolls, or an on-demand scrollbar takes 15px
           off the cards in one scope and gives it back in another. -->
      <div
        #suggestionQueue
        class="flex flex-col space-y-3 overflow-y-auto [scrollbar-gutter:stable] max-h-[60vh] overscroll-contain"
        data-testid="suggestionQueue"
      >
        <!-- The summary bar's role="status" line already announces this state, so this copy is
             silent: a second live region would announce the last decision twice. -->
        <p
          class="px-3 py-6 text-xs text-center text-project-2300"
          *ngIf="isSmartLinkAllReviewed && smartLinkScope === 'review'"
          data-testid="smartLinkAllReviewedState"
        >
          Nothing left to review.
        </p>

        <!-- Silent, like the all-reviewed copy above: the scope was opened deliberately, and the
             summary bar's role="status" line is the one region that speaks. -->
        <ng-container *ngIf="smartLinkScope === 'linked'">
          <p
            class="px-3 py-6 text-xs text-center text-project-2300"
            *ngIf="smartLinkLinkedCount === 0"
            data-testid="linkedScopeEmptyState"
          >
            Nothing linked yet. Suggestions you accept are listed here.
          </p>

          <div class="flex flex-col" *ngIf="smartLinkLinkedCount > 0">
            <div
              class="sticky top-0 z-10 flex items-center py-1.5 bg-white border-b border-project-600"
              data-testid="linkedScopeHeader"
            >
              <p class="text-xs font-semibold text-project-2000">
                Linked ·
                <span
                  class="font-normal text-project-2400"
                  data-testid="linkedScopeCount"
                  >{{ smartLinkLinkedCount }}</span
                >
              </p>
            </div>
            <div class="flex flex-col pt-2 space-y-2">
              <ng-container
                *ngTemplateOutlet="
                  suggestionCard;
                  context: {
                    groups: smartLinkSingletons(linkedSmartLinks),
                    showTypeChip: true
                  }
                "
              ></ng-container>
            </div>
            <!-- Said once at the foot of the scope rather than on every card: the reversal is
                 local, so a reload re-applies the accept. -->
            <p
              class="pt-2.5 text-xxs leading-4 text-project-2400"
              data-testid="linkedScopeNote"
            >
              Unlinking removes the record from Related data. The scan still
              records it as accepted.
            </p>
          </div>
        </ng-container>

        <ng-container *ngIf="smartLinkScope === 'dismissed'">
          <p
            class="px-3 py-6 text-xs text-center text-project-2300"
            *ngIf="smartLinkDismissedCount === 0"
            data-testid="dismissedScopeEmptyState"
          >
            Nothing dismissed. Suggestions you reject are kept here.
          </p>

          <div class="flex flex-col" *ngIf="smartLinkDismissedCount > 0">
            <div
              class="sticky top-0 z-10 flex items-center py-1.5 bg-white border-b border-project-600"
              data-testid="dismissedScopeHeader"
            >
              <p class="text-xs font-semibold text-project-2000">
                Dismissed ·
                <span
                  class="font-normal text-project-2400"
                  data-testid="dismissedScopeCount"
                  >{{ smartLinkDismissedCount }}</span
                >
              </p>
            </div>
            <div class="flex flex-col pt-2 space-y-2">
              <ng-container
                *ngTemplateOutlet="
                  suggestionCard;
                  context: {
                    groups: smartLinkSingletons(dismissedSmartLinks),
                    showTypeChip: true
                  }
                "
              ></ng-container>
            </div>
          </div>
        </ng-container>

        <ng-container *ngIf="smartLinkScope === 'review'">
          <div
            class="flex flex-col pt-3 border-t border-project-600 first:pt-0 first:border-t-0"
            *ngFor="let group of suggestionGroups"
            data-testid="suggestionGroup"
            [attr.data-group]="group.label"
          >
            <!-- bg-white is load-bearing now that the queue scrolls: cards pass underneath. -->
            <div
              class="sticky top-0 z-10 flex flex-wrap items-center justify-between py-1.5 gap-x-2 gap-y-1 bg-white border-b border-project-600"
              data-testid="suggestionGroupHeader"
            >
              <p class="text-xs font-semibold text-project-2000">
                {{ group.label }} ·
                <span
                  class="font-normal text-project-2400"
                  data-testid="suggestionGroupCount"
                  >{{ group.items.length }}</span
                >
              </p>
              <ng-container
                *ngIf="isEditMode && group.strongAndPossibleItems.length >= 2"
              >
                <button
                  type="button"
                  class="flex items-center h-6 px-2 border rounded gap-x-1 text-xxs font-semibold whitespace-nowrap shrink-0 text-project-4400 border-project-4500 bg-project-4600 hover:cursor-pointer hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-project-300"
                  data-testid="acceptAllBtn"
                  [attr.aria-label]="'Accept all suggestions in ' + group.label"
                  [matMenuTriggerFor]="acceptScopeMenu"
                >
                  <i class="icon icon-check" aria-hidden="true"></i> Accept all
                  <i class="icon icon-caret-down" aria-hidden="true"></i>
                </button>
                <mat-menu
                  #acceptScopeMenu="matMenu"
                  class="bg-white border rounded shadow-lg border-project-600"
                  [aria-label]="'How much of ' + group.label + ' to accept?'"
                >
                  <ng-template matMenuContent>
                    <button
                      mat-menu-item
                      type="button"
                      class="h-8 px-3 leading-8 min-h-0 text-xxs text-project-2400 hover:cursor-pointer hover:bg-project-2500"
                      *ngFor="let scope of group.acceptScopes"
                      [attr.data-testid]="'acceptScope-' + scope.key"
                      [title]="acceptScopePreview(scope)"
                      (click)="onAcceptScope(scope)"
                    >
                      {{ scope.label }}
                    </button>
                    <p
                      class="px-3 py-1 text-xxs text-project-1100"
                      *ngIf="group.ambiguousCount > 0"
                      data-testid="acceptScopeAmbiguousNote"
                    >
                      Excludes {{ group.ambiguousCount }} ambiguous
                    </p>
                  </ng-template>
                </mat-menu>
              </ng-container>
            </div>
            <div class="flex flex-col pt-2 space-y-2">
              <!-- One block at a time rather than one flat list of groups, so the card stays the
                   direct child that space-y-2 separates. -->
              <ng-container
                *ngFor="let block of group.blocks"
                [ngTemplateOutlet]="suggestionCard"
                [ngTemplateOutletContext]="{
                  groups: [block.items],
                  showTypeChip: false
                }"
              ></ng-container>
            </div>
          </div>

          <!-- A section of the queue like any other, not a disclosure: the review count includes
               these, so a toggle would promise work and then hide it. -->
          <div
            class="flex flex-col pt-3 border-t border-project-600 first:pt-0 first:border-t-0"
            *ngIf="weakSmartLinks.length > 0"
            data-testid="weakMatchesSection"
          >
            <div
              class="sticky top-0 z-10 flex flex-wrap items-center justify-between py-1.5 gap-x-2 gap-y-1 bg-white border-b border-project-600"
              data-testid="weakMatchesHeader"
            >
              <p class="text-xs font-semibold text-project-2000">
                Weaker matches ·
                <span
                  class="font-normal text-project-2400"
                  data-testid="weakMatchesCount"
                  >{{ weakSmartLinks.length }}</span
                >
              </p>
              <button
                *ngIf="isEditMode"
                type="button"
                class="flex items-center h-6 px-2 border rounded gap-x-1 text-xxs font-semibold whitespace-nowrap shrink-0 border-project-600 text-project-2400 hover:cursor-pointer hover:text-red-600 hover:border-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-project-300"
                data-testid="dismissAllWeakBtn"
                aria-label="Dismiss all weaker matches"
                [matMenuTriggerFor]="weakReasonMenu"
              >
                <i class="icon icon-times" aria-hidden="true"></i> Dismiss all ({{
                  weakSmartLinks.length
                }})
                <i class="icon icon-caret-down" aria-hidden="true"></i>
              </button>
              <mat-menu
                #weakReasonMenu="matMenu"
                class="bg-white border rounded shadow-lg border-project-600"
                aria-label="Why reject all weaker matches?"
              >
                <button
                  mat-menu-item
                  type="button"
                  class="h-8 px-3 leading-8 min-h-0 text-xxs text-project-2400 hover:cursor-pointer hover:bg-project-2500"
                  *ngFor="let reason of rejectReasons"
                  [attr.data-testid]="'weakReason-' + reason.value"
                  (click)="onDismissAllWeak(reason.value)"
                >
                  {{ reason.label }}
                </button>
              </mat-menu>
            </div>
            <div class="flex flex-col pt-2 space-y-2">
              <ng-container
                *ngTemplateOutlet="
                  suggestionCard;
                  context: {
                    groups: smartLinkSingletons(weakSmartLinks),
                    showTypeChip: true
                  }
                "
              ></ng-container>
            </div>
          </div>
        </ng-container>
      </div>
    </div>
  </div>
  <div class="flex-1 min-w-0" data-testid="preview" notExpandedContent>
    <div
      class="flex flex-col w-full py-4 m-0 space-y-4 empty:py-0 bg-project-2200"
    >
      <ng-container *ngIf="isSmartLinkingEnabled">
        <div
          class="flex flex-col w-full px-2.5 py-2 gap-y-1.5 bg-white border rounded border-project-600"
          *ngIf="isSmartLinkBusy"
          data-testid="smartLinkPreviewBusy"
          [attr.data-phase]="smartLinkStatus"
        >
          <span
            class="relative block w-full h-1 overflow-hidden rounded-full bg-project-2900"
            aria-hidden="true"
          >
            <span class="block h-full smart-link-sweep bg-project-300"></span>
          </span>
          <span
            class="flex items-center text-xs font-semibold gap-x-1.5 text-project-2300"
          >
            <app-ai-icon class="text-inherit shrink-0"></app-ai-icon>
            {{ smartLinkPreviewBusyText }}
          </span>
        </div>
        <button
          type="button"
          class="flex flex-col w-full px-2.5 py-2 gap-y-1.5 text-left bg-white border rounded border-project-600 hover:border-project-300 hover:cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-project-300"
          *ngIf="smartLinkPendingCount > 0"
          data-testid="previewReviewStrip"
          (click)="onPreviewReview($event)"
        >
          <span
            class="flex items-center w-full text-xs font-semibold gap-x-1.5 text-project-300"
          >
            <app-ai-icon class="text-inherit shrink-0"></app-ai-icon>
            <span data-testid="previewReviewLine"
              >{{ smartLinkPendingCount }} smart
              {{ smartLinkPendingCount === 1 ? "link" : "links" }} to review</span
            >
            <span class="flex items-center ml-auto gap-x-1 shrink-0"
              >Review<i class="icon icon-angle-right" aria-hidden="true"></i
            ></span>
          </span>
          <!-- pendingGroupCounts, not suggestionGroups: it spans every undecided suggestion, so
               these counts always sum to the line above. A single group would only restate it,
               hence the > 1 gate — which also keeps the tally's "databases" plural. -->
          <span
            *ngIf="pendingGroupCounts.length > 1"
            class="flex flex-wrap pl-5 gap-x-3 gap-y-0.5 text-xxs text-project-2400"
            aria-label="Suggestions by database"
            data-testid="previewReviewBreakdown"
          >
            <ng-container
              *ngIf="
                pendingGroupCounts.length <= MAX_NAMED_SUGGESTION_GROUPS;
                else groupTally
              "
            >
              <span
                *ngFor="let group of pendingGroupCounts"
                data-testid="previewReviewBreakdownEntry"
              >
                {{ group.label }}
                <span class="font-medium tabular-nums text-project-2000">{{
                  group.count
                }}</span>
              </span>
            </ng-container>
            <ng-template #groupTally>
              <span data-testid="previewReviewBreakdownTally">
                across
                <span class="font-medium tabular-nums text-project-2000">{{
                  pendingGroupCounts.length
                }}</span>
                databases
              </span>
            </ng-template>
          </span>
        </button>
      </ng-container>
      <div class="text-xs" *ngIf="managerListControl.value.length > 0">
        <b>Managers</b>
        <div *ngFor="let manager of getSelectedManagers()">
          <p class="text-project-2400" data-testid="managerName">
            {{ manager.firstName + " " + manager.lastName
            }}<span
              class="text-project-2400"
              *ngIf="handleManagerRating(manager.rating)"
              data-testid="managerRating"
            >
              {{ " - " + manager.rating }}
            </span>
            <app-ai-icon
              *ngIf="isSmartLinked('manager', manager.id)"
              class="text-project-2100"
              label="Added by smart linking"
              data-testid="previewSmartLinkedMarker"
            ></app-ai-icon>
          </p>
        </div>
      </div>
      <div class="text-xs" *ngIf="fundListControl.value.length > 0">
        <b>Funds/Etfs</b>
        <p
          class="text-project-2400"
          *ngFor="let fund of fundListControl.value"
          data-testid="fundName"
        >
          {{ fund.name }}
          <app-ai-icon
            *ngIf="isSmartLinked('fund', fund.id)"
            class="text-project-2100"
            label="Added by smart linking"
            data-testid="previewSmartLinkedMarker"
          ></app-ai-icon>
        </p>
      </div>
      <div class="text-xs" *ngIf="sectorListControl.value.length > 0">
        <b>Sectors</b>
        <p
          class="text-project-2400"
          *ngFor="let sector of sectorListControl.value"
          data-testid="sectorName"
        >
          {{ sector.name }}
          <app-ai-icon
            *ngIf="isSmartLinked('sector', sector.id)"
            class="text-project-2100"
            label="Added by smart linking"
            data-testid="previewSmartLinkedMarker"
          ></app-ai-icon>
        </p>
      </div>
      <div class="text-xs" *ngIf="assetClassListControl.value.length > 0">
        <b>Asset Class</b>
        <p
          class="text-project-2400"
          *ngFor="let assetClass of assetClassListControl.value"
          data-testid="assetClassName"
        >
          {{ assetClass.name }}
          <app-ai-icon
            *ngIf="isSmartLinked('assetClass', assetClass.id)"
            class="text-project-2100"
            label="Added by smart linking"
            data-testid="previewSmartLinkedMarker"
          ></app-ai-icon>
        </p>
      </div>
      <div class="text-xs" *ngIf="investmentTrustListControl.value.length > 0">
        <b>Investment Trust</b>
        <p
          class="text-project-2400"
          *ngFor="let invTrust of investmentTrustListControl.value"
          data-testid="investmentTrustName"
        >
          {{ invTrust.name }}
          <app-ai-icon
            *ngIf="isSmartLinked('investmentTrust', invTrust.id)"
            class="text-project-2100"
            label="Added by smart linking"
            data-testid="previewSmartLinkedMarker"
          ></app-ai-icon>
        </p>
      </div>
      <div class="text-xs" *ngIf="shareListControl.value.length > 0">
        <b>Share</b>
        <p
          class="text-project-2400"
          *ngFor="let share of shareListControl.value"
          data-testid="shareName"
        >
          {{ share.companyName }}
        </p>
      </div>
      <div
        class="text-xs"
        *ngIf="
          privateMarketsFundListControl &&
          privateMarketsFundListControl.value.length > 0
        "
      >
        <b>Private Markets Funds</b>
        <p
          class="text-project-2400"
          *ngFor="let privateMarketsFund of privateMarketsFundListControl.value"
          data-testid="privateMarketsFundName"
        >
          {{ privateMarketsFund.name }}
          <app-ai-icon
            *ngIf="isSmartLinked('privateMarketsFund', privateMarketsFund.id)"
            class="text-project-2100"
            label="Added by smart linking"
            data-testid="previewSmartLinkedMarker"
          ></app-ai-icon>
        </p>
      </div>
    </div>
  </div>
</app-expandable-section-box>

<!-- Outside every *ngIf on purpose: this region must already be in the accessibility tree
     before its text changes, and both the tabpanel and the busy row are inserted in the
     same change-detection pass as the run starts. -->
<span
  class="sr-only"
  role="status"
  aria-atomic="true"
  data-testid="smartLinkBusyAnnouncement"
  >{{ smartLinkBusyAnnouncement }}</span
>

<!-- Decoration, not a progressbar: the API reports no fraction, so the sweep carries no role
     and no value. The announcement above is what conveys meaning. -->
<ng-template #smartLinkSweep>
  <span
    class="absolute bottom-0 left-0 right-0 overflow-hidden h-0.5 bg-project-2900"
    aria-hidden="true"
  >
    <span class="block h-full smart-link-sweep bg-project-300"></span>
  </span>
</ng-template>

<!-- One definition for every scope that renders cards, with all four outputs bound: the card
     renders Restore only when dismissed, Unlink only when linked and Accept/Reject only when
     neither, so no scope can fire the wrong one. -->
<ng-template #suggestionCard let-groups="groups" let-showTypeChip="showTypeChip">
  <app-smart-link-card
    *ngFor="let group of groups; trackBy: trackSuggestionGroup"
    [candidates]="smartLinkCandidates(group)"
    [showTypeChip]="showTypeChip"
    [isEditMode]="isEditMode"
    [decidingIds]="decisionsInFlight"
    [scanContent]="smartLinkScanSnapshot"
    [alternativeNames]="alternativeNames(group[0])"
    (handleAccept)="onAcceptSuggestion($event)"
    (handleReject)="onRejectSuggestion($event)"
    (handleRestore)="onRestoreSuggestion($event)"
    (handleUnlink)="onUnlinkSuggestion($event)"
    (handleFlashMention)="onFlashMention($event)"
  ></app-smart-link-card>
</ng-template>
